<template>
  <NdsButton
    class="mainHeaderNotifications"
    :color="NdsButtonColor.TERTIARY"
    :size="NdsButtonSize.S"
    @click="goToActionsToReview"
  >
    <NdsIcon
      class="mainHeaderNotifications__icon"
      :icon="NdsIconType.CLIPBOARD_CHECK"
      :size="NdsIconSize.S"
    />

    <transition name="fade-input">
      <NdsSpinner
        v-if="isFirstLoading"
        class="mainHeaderNotifications__badge"
        :size="NdsSpinnerSize.COMPACT"
      />

      <NdsBadge
        v-else
        class="mainHeaderNotifications__badge"
        :color="badgeColor"
        :count="count"
        :size="NdsBadgeSize.M"
      />
    </transition>
  </NdsButton>
</template>

<script lang="ts" setup>
import { computed, watch } from 'vue'
import { storeToRefs } from 'pinia'

import store from '@/store'
import { useRoute, useRouter } from 'vue-router/composables'
import { useActionsInReviewStore } from '@/contexts/actions/stores/ActionsInReview.store'
import { useActionsFiltersStore } from '@/contexts/action-plans/stores/ActionsFilters.store'
import { useActionsListFiltersStore } from '@/contexts/action-plans/stores/ActionsListFilters.store'

import {
  ACTION_PLAN_ALL_ID,
  ACTION_PLAN_DISPLAY_VIEWS,
  ACTION_PLAN_ROUTE_NAME,
  ACTION_STATUS,
} from '@/contexts/action-plans/ActionPlans.constants'

import NdsIcon from '@/components/NewDesignSystem/NdsIcon/NdsIcon.vue'
import { NdsIconSize, NdsIconType } from '@/components/NewDesignSystem/NdsIcon/NdsIcon.interfaces'
import NdsBadge from '@/components/NewDesignSystem/NdsBadge/NdsBadge.vue'
import { NdsBadgeColor, NdsBadgeSize } from '@/components/NewDesignSystem/NdsBadge/NdsBadge.interfaces'
import NdsButton from '@/components/NewDesignSystem/NdsButton/NdsButton.vue'
import { NdsButtonColor, NdsButtonSize } from '@/components/NewDesignSystem/NdsButton/NdsButton.interfaces'
import NdsSpinner from '@/components/NewDesignSystem/NdsSpinner/NdsSpinner.vue'
import { NdsSpinnerSize } from '@/components/NewDesignSystem/NdsSpinner/NdsSpinner.interfaces'

const route = useRoute()
const router = useRouter()
const actionsInReviewStore = useActionsInReviewStore()
const { count, isLoading, hasLoadedOnce } = storeToRefs(actionsInReviewStore)
const { removeActionsFiltersById } = useActionsFiltersStore()
const { removeActionsListFilterById } = useActionsListFiltersStore()

const isFirstLoading = computed(() => isLoading.value && !hasLoadedOnce.value)

const badgeColor = computed(() => (count.value === 0 ? NdsBadgeColor.SUCCESS : NdsBadgeColor.DANGER))

const goToActionsToReview = () => {
  const customerId = store.getters.customer_id
  removeActionsFiltersById(ACTION_PLAN_ALL_ID, customerId)
  removeActionsListFilterById(ACTION_PLAN_ALL_ID, customerId)

  router.push({
    name: ACTION_PLAN_ROUTE_NAME,
    params: { id: ACTION_PLAN_ALL_ID },
    query: {
      tab: ACTION_PLAN_DISPLAY_VIEWS.LIST,
      statuses: [ACTION_STATUS.TO_REVIEW],
    },
  })
}

watch(
  () => route.path,
  () => {
    if (!store.getters.isOnlyContributor) {
      actionsInReviewStore.refresh()
    }
  },
  { immediate: true }
)
</script>

<style lang="scss" scoped>
.mainHeaderNotifications {
  display: inline-flex;
  position: relative;

  &__icon {
    color: var(--danger-500);
  }

  &__badge {
    position: absolute;
    top: 50%;
    right: 0;
  }
}
</style>
